import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db1s7iuoc082o2i1ke10";

const interests = [
  "Sales & Acquisitions",
  "Distribution",
  "Press & Media",
  "Production Partnership",
  "Other",
];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const message =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = String(message).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setFormError("Network error. Please check your connection and try again.");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors";

  return (
    <section id="contact" className="scroll-mt-20 bg-background-50 py-16 md:py-24">
      <div className="px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <span className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.3em] uppercase text-primary-500">
              <span className="w-6 h-px bg-primary-500" />
              Get in Touch
            </span>
            <h2 className="mt-3 font-heading font-extrabold uppercase tracking-tight text-foreground-950 text-3xl md:text-5xl leading-[1.02]">
              Let's talk about your slate
            </h2>
            <p className="mt-5 text-sm md:text-base text-foreground-600 max-w-lg leading-relaxed">
              Whether you're a buyer, festival programmer or production partner, our team
              is ready to help. Reach out and we'll get back to you shortly.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href="mailto:team@thevoltagepictures.com"
                className="flex items-center gap-4 rounded-lg bg-background-100 px-5 py-4 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <span>
                  <span className="block font-label text-[11px] tracking-[0.16em] uppercase text-foreground-500">
                    Email
                  </span>
                  <span className="block text-sm font-medium text-foreground-950">
                    team@thevoltagepictures.com
                  </span>
                </span>
              </a>
              <a
                href="tel:+13234648351"
                className="flex items-center gap-4 rounded-lg bg-background-100 px-5 py-4 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-accent-100 text-accent-700 shrink-0">
                  <i className="ri-phone-line text-xl"></i>
                </span>
                <span>
                  <span className="block font-label text-[11px] tracking-[0.16em] uppercase text-foreground-500">
                    Phone
                  </span>
                  <span className="block text-sm font-medium text-foreground-950">
                    (323) 464-8351
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-lg bg-background-100 px-5 py-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-secondary-100 text-secondary-800 shrink-0">
                  <i className="ri-map-pin-line text-xl"></i>
                </span>
                <span>
                  <span className="block font-label text-[11px] tracking-[0.16em] uppercase text-foreground-500">
                    Studio
                  </span>
                  <span className="block text-sm font-medium text-foreground-950">
                    6360 Deep Dell Place, Los Angeles, CA 90068
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-background-100 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center text-center py-14">
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-check-line text-3xl"></i>
                </span>
                <h3 className="mt-5 font-heading font-bold text-xl text-foreground-950">
                  Message sent
                </h3>
                <p className="mt-2 text-sm text-foreground-600 max-w-sm">
                  Thanks for reaching out. Our team will be in touch shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 rounded-md border border-foreground-950 px-5 py-3 font-label text-xs tracking-[0.18em] uppercase text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                id="voltage-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="vp-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Full name
                    </label>
                    <input
                      id="vp-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Jane Doe"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="vp-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Email
                    </label>
                    <input
                      id="vp-email"
                      name="email"
                      type="email"
                      required
                      placeholder="jane@company.com"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="vp-company" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      Company
                    </label>
                    <input
                      id="vp-company"
                      name="company"
                      type="text"
                      placeholder="Company name"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="vp-interest" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      I'm interested in
                    </label>
                    <select id="vp-interest" name="interest" className={inputClass} defaultValue={interests[0]}>
                      {interests.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="vp-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="vp-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Tell us a little about what you're looking for..."
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-verify-alt"
                />

                {status === "error" && (
                  <p className="text-sm text-primary-600">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-70 text-background-50 font-label text-xs tracking-[0.18em] uppercase px-6 py-4 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === "submitting" ? "Sending..." : "Send Inquiry"}
                  {status !== "submitting" && <i className="ri-send-plane-fill text-base"></i>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}